import type { AdminCrewReportDeskBarChartProps } from "../../models/admin-crew-report.models";
import {
  getAdminCrewReportDeskBarAccessibleSummary,
  getAdminCrewReportDeskBarGap,
  getAdminCrewReportDeskBarHeight,
  getAdminCrewReportDeskColor,
} from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_DESK_BAR_PLOT_DEFAULT_HEIGHT_PX,
  ADMIN_CREW_REPORT_DESK_BAR_PLOT_MIN_HEIGHT_PX,
  ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX,
  ADMIN_CREW_REPORT_DESK_LEGEND_COMPACT_COLUMN_GAP_PX,
  ADMIN_CREW_REPORT_DESK_LEGEND_FONT_SIZES,
  ADMIN_CREW_REPORT_DESK_LEGEND_MAX_HEIGHT_PX,
  ADMIN_CREW_REPORT_DESK_LEGEND_MAX_VISIBLE_ROWS,
  ADMIN_CREW_REPORT_DESK_LEGEND_NORMAL_COLUMN_GAP_PX,
  ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
} from "../../utils/admin-crew-report.constants";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const AdminCrewReportDeskBarChart = ({
  title,
  categories,
  emptyStateMessage,
  accessibleTotalText,
  accessibleValueKind,
  cardDataTest,
  cardTestId,
  visualizationTestIdPrefix,
  className,
}: AdminCrewReportDeskBarChartProps) => {
  const visibleCategories = useMemo(() => categories.filter((category) => category.value > 0), [categories]);
  const legendViewportRef = useRef<HTMLDivElement>(null);
  const legendListRef = useRef<HTMLUListElement>(null);
  const [legendFontSize, setLegendFontSize] = useState<number>(ADMIN_CREW_REPORT_DESK_LEGEND_FONT_SIZES[0]);
  const [legendColumnGap, setLegendColumnGap] = useState(ADMIN_CREW_REPORT_DESK_LEGEND_NORMAL_COLUMN_GAP_PX);
  const [legendViewportMaxHeight, setLegendViewportMaxHeight] = useState(ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX);
  const [barPlotHeight, setBarPlotHeight] = useState(ADMIN_CREW_REPORT_DESK_BAR_PLOT_DEFAULT_HEIGHT_PX);
  const [isLegendScrollable, setIsLegendScrollable] = useState(false);
  const maximumValue = Math.max(0, ...visibleCategories.map((category) => category.value));
  const accessibleSummary = getAdminCrewReportDeskBarAccessibleSummary(visibleCategories, accessibleValueKind);
  const gridTemplateColumns = `repeat(${visibleCategories.length}, minmax(0, 1fr))`;
  const columnGap = `${getAdminCrewReportDeskBarGap(visibleCategories.length)}px`;

  const updateLegendLayout = useCallback(() => {
    const legendList = legendListRef.current;
    if (!legendList) return;

    const legendLabels = Array.from(
      legendList.querySelectorAll<HTMLElement>("[data-admin-crew-report-desk-legend-label]"),
    );
    const legendItems = Array.from(
      legendList.querySelectorAll<HTMLElement>("[data-admin-crew-report-desk-legend-item]"),
    );
    const minimumLegendFontSize = ADMIN_CREW_REPORT_DESK_LEGEND_FONT_SIZES.at(-1) ?? 9.5;
    let nextFontSize = minimumLegendFontSize;
    let nextColumnGap = ADMIN_CREW_REPORT_DESK_LEGEND_COMPACT_COLUMN_GAP_PX;

    for (const candidateFontSize of ADMIN_CREW_REPORT_DESK_LEGEND_FONT_SIZES) {
      legendList.style.fontSize = `${candidateFontSize}px`;
      legendList.style.columnGap = `${ADMIN_CREW_REPORT_DESK_LEGEND_NORMAL_COLUMN_GAP_PX}px`;
      const labelsFit = legendLabels.every((label) => label.scrollWidth <= label.clientWidth);

      if (legendList.scrollHeight <= ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX && labelsFit) {
        nextFontSize = candidateFontSize;
        nextColumnGap = ADMIN_CREW_REPORT_DESK_LEGEND_NORMAL_COLUMN_GAP_PX;
        break;
      }
    }

    legendList.style.fontSize = `${nextFontSize}px`;
    legendList.style.columnGap = `${nextColumnGap}px`;
    const naturalLegendHeight = legendList.scrollHeight || ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX;
    const legendRowOffsets = Array.from(new Set(legendItems.map((legendItem) => legendItem.offsetTop))).sort(
      (left, right) => left - right,
    );
    const hasMoreThanMaximumVisibleRows = legendRowOffsets.length > ADMIN_CREW_REPORT_DESK_LEGEND_MAX_VISIBLE_ROWS;
    const firstRowOffset = legendRowOffsets[0] ?? 0;
    const lastVisibleRowOffset = legendRowOffsets[ADMIN_CREW_REPORT_DESK_LEGEND_MAX_VISIBLE_ROWS - 1];
    const lastVisibleRowBottom =
      lastVisibleRowOffset === undefined
        ? naturalLegendHeight
        : Math.max(
            ...legendItems
              .filter((legendItem) => legendItem.offsetTop === lastVisibleRowOffset)
              .map((legendItem) => legendItem.offsetTop + legendItem.offsetHeight - firstRowOffset),
          );
    const maximumVisibleLegendHeight = Math.min(
      ADMIN_CREW_REPORT_DESK_LEGEND_MAX_HEIGHT_PX,
      hasMoreThanMaximumVisibleRows ? lastVisibleRowBottom : naturalLegendHeight,
    );
    const nextLegendViewportMaxHeight = Math.max(
      ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX,
      maximumVisibleLegendHeight,
    );
    const additionalLegendHeight = nextLegendViewportMaxHeight - ADMIN_CREW_REPORT_DESK_LEGEND_BASE_HEIGHT_PX;
    const nextBarPlotHeight = Math.max(
      ADMIN_CREW_REPORT_DESK_BAR_PLOT_MIN_HEIGHT_PX,
      ADMIN_CREW_REPORT_DESK_BAR_PLOT_DEFAULT_HEIGHT_PX - additionalLegendHeight,
    );
    const nextIsLegendScrollable =
      hasMoreThanMaximumVisibleRows || naturalLegendHeight > ADMIN_CREW_REPORT_DESK_LEGEND_MAX_HEIGHT_PX;

    setLegendFontSize(nextFontSize);
    setLegendColumnGap(nextColumnGap);
    setLegendViewportMaxHeight(nextLegendViewportMaxHeight);
    setBarPlotHeight(nextBarPlotHeight);
    setIsLegendScrollable(nextIsLegendScrollable);
  }, []);

  useLayoutEffect(() => {
    updateLegendLayout();
  }, [updateLegendLayout, visibleCategories]);

  useEffect(() => {
    const legendViewport = legendViewportRef.current;
    if (!legendViewport) return;

    let isActive = true;
    const updateActiveLegendLayout = () => {
      if (isActive) updateLegendLayout();
    };
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateActiveLegendLayout);

    if (resizeObserver) {
      resizeObserver.observe(legendViewport);
    } else {
      window.addEventListener("resize", updateActiveLegendLayout);
    }

    void document.fonts?.ready.then(updateActiveLegendLayout);

    return () => {
      isActive = false;
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateActiveLegendLayout);
    };
  }, [updateLegendLayout, visibleCategories]);

  return (
    <article
      data-test={cardDataTest}
      data-testid={cardTestId}
      className={`flex ${ADMIN_CREW_REPORT_STATISTICS_CARD_HEIGHT_CLASS_NAME} w-full flex-col justify-self-start overflow-hidden border border-[#D8E6E3] bg-white px-4 pb-4 pt-[11px] dark:border-gray-700 dark:bg-gray-800 md:max-w-sm${className ? ` ${className}` : ""}`}
    >
      <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>{title}</h2>

      {visibleCategories.length === 0 ? (
        <div className="flex h-44 items-center justify-center px-4 text-center text-sm text-[#65706F] dark:text-gray-300">
          {emptyStateMessage}
        </div>
      ) : (
        <div className="mt-3 flex min-h-0 flex-1 flex-col">
          <div
            ref={legendViewportRef}
            role={isLegendScrollable ? "region" : undefined}
            aria-label={isLegendScrollable ? `Scrollable ${title.toLowerCase()} legend` : undefined}
            data-test={`${visualizationTestIdPrefix}-scrollable-legend`}
            data-testid={`${visualizationTestIdPrefix}-scrollable-legend`}
            tabIndex={isLegendScrollable ? 0 : undefined}
            className={`min-h-0 max-w-full flex-none overflow-x-hidden pr-1 [scrollbar-gutter:stable] [scrollbar-width:thin] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#367C2B] [&::-webkit-scrollbar]:w-1.5 ${
              isLegendScrollable ? "overflow-y-auto" : "overflow-y-hidden"
            }`}
            style={{ maxHeight: `${legendViewportMaxHeight}px` }}
          >
            <ul
              ref={legendListRef}
              aria-hidden="true"
              className={`flex min-w-0 flex-wrap items-center justify-center gap-y-1 leading-normal ${
                legendColumnGap === ADMIN_CREW_REPORT_DESK_LEGEND_COMPACT_COLUMN_GAP_PX ? "gap-x-2" : "gap-x-3"
              }`}
              style={{ columnGap: `${legendColumnGap}px`, fontSize: `${legendFontSize}px` }}
            >
              {visibleCategories.map((category) => (
                <li
                  key={category.name}
                  data-admin-crew-report-desk-legend-item
                  className="flex min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap text-[#475351] dark:text-gray-300"
                >
                  <span
                    data-testid={`${visualizationTestIdPrefix}-legend-${category.name}`}
                    className="h-2 w-2 flex-none rounded-full"
                    style={{ backgroundColor: getAdminCrewReportDeskColor(category.name) }}
                  />
                  <span data-admin-crew-report-desk-legend-label className="min-w-0 truncate" title={category.name}>
                    {category.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div
            role="img"
            aria-label={`${title} distribution. ${accessibleSummary}. ${accessibleTotalText}.`}
            className="mt-auto flex-none pt-2"
          >
            <div
              data-testid={`${visualizationTestIdPrefix}-bars`}
              aria-hidden="true"
              className="admin-crew-report-desk-bar-plot grid min-w-0 border-b border-[#8AA6A1] pt-6 dark:border-gray-500"
              style={
                {
                  "--admin-crew-report-desk-bar-plot-height": `${barPlotHeight}px`,
                  gridTemplateColumns,
                  columnGap,
                  height: `${barPlotHeight}px`,
                } as CSSProperties
              }
            >
              {visibleCategories.map((category) => {
                const barHeight = getAdminCrewReportDeskBarHeight(category.value, maximumValue);
                const color = getAdminCrewReportDeskColor(category.name);

                return (
                  <div key={category.name} className="relative min-w-0">
                    <span
                      className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-[#24302E] dark:text-gray-100"
                      style={{ bottom: `calc(${barHeight}% + 0.25rem)` }}
                    >
                      {category.value}
                    </span>
                    <span
                      data-test={`${visualizationTestIdPrefix}-bar-${category.name}`}
                      data-testid={`${visualizationTestIdPrefix}-bar-${category.name}`}
                      className="absolute bottom-0 left-1/2 w-5 max-w-[65%] -translate-x-1/2 sm:w-6"
                      style={{
                        backgroundColor: color,
                        height: `${barHeight}%`,
                        minHeight: category.value > 0 ? "2px" : undefined,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default AdminCrewReportDeskBarChart;
